import React from 'react';
import EditListingModal from './EditListingModal';
import PublishModal from './PublishModal';
import CancelListingModal from './CancelListingModal';
import { Table, Button } from 'antd';
import ColumnSelection from './ColumnSelection';
import TagSearch from './TagSearch';
import ChannelListings from './ChannelListings';
import { getProducts, getChannels, getSettings } from '../api';
import { filterResults, retrieve, fieldSort } from '../util';
import XLSX from 'xlsx';

class Listings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filteredInfo: {},
      sortedInfo: {},
      pageInfo: {
        defaultPageSize: 50,
        showSizeChanger: true,
        pageSizeOptions: ['25', '50', '100', '200'],
      },
      loading: false,
      channels: [],
      settings: [],
      themes: [],
      data: [],
      showHeaderSelection: false,
      columns: [],
      columnList: [],
      activeColumns: [],
      columnDirectory: {},
      rowSelections: [],
      selectedRowKeys: [],
      search: '',
      filterList: {},
    };
  }

  componentDidMount() {
    this.setState({ loading: true });
    Promise.all([this.fetch(), this.fetchSettings()]).then(() => {
      this.setState({ loading: false });
    });
  }

  fetchChannels = async () => {
    try {
      const channels = await getChannels();
      return channels;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  fetchSettings = async () => {
    try {
      const { settings, themes } = await getSettings();
      this.setState({ settings, themes });
    } catch (err) {
      console.error(err);
    }
  };

  fetch = async () => {
    this.setState({ loading: true });
    const filterTopLevel = {
      storeViewId: {},
      productType: {},
      category: {},
      categoryName: {},
    };
    const filterCustom = {
      carrier: {},
      color: {},
      condition: {},
      make: {},
      model: {},
      os: {},
      storage: {},
      type: {},
      kit: {},
    };
    const products = await getProducts();
    const channels = await this.fetchChannels();
    // const count = products.reduce((num, product) => {
    //   return num + Math.max(0, product.quantity);
    // }, 0);
    // console.log(count);

    let data = [...products];
    let columnPathDirectory = {};
    data.forEach((item) => {
      for (let key in filterTopLevel) {
        if (item[key]) {
          filterTopLevel[key][item[key]] = true;
        }
      }
      if (item.custom) {
        for (let key in filterCustom) {
          if (item.custom[key]) {
            filterCustom[key][item.custom[key]] = true;
          }
        }
      }

      // set dateUpdated
      for (const channel of channels) {
        const product = (channel.products || []).find(
          (p) => p.sku.trim() === item.sku
        );
        if (product) {
          item.dateUpdated = product.dateUpdated;
          break;
        }
      }

      // set active listings
      item.listings = channels
        .filter((channel) =>
          (channel.products || []).find(
            (p) => p.sku.trim() === item.sku && p.listed === '1'
          )
        )
        .map((channel) => channel.id);
    });

    // append listings that are no longer in product list
    const missingListedProducts = {};
    for (const channel of channels) {
      (channel.products || [])
        .filter(
          (p) =>
            p.listed === '1' && !data.find((item) => p.sku.trim() === item.sku)
        )
        .map((listing) => {
          if (!missingListedProducts[listing.sku]) {
            missingListedProducts[listing.sku] = {
              sku: listing.sku,
              listings: [channel.id],
              quantity: 0,
              remoteId: listing.id,
              dateUpdated: listing.dateUpdated,
            };
          } else {
            missingListedProducts[listing.sku].listings.push(channel.id);
          }
        });
    }
    Object.keys(missingListedProducts).forEach((id) => {
      data.push(missingListedProducts[id]);
    });

    const filterList = {};
    const fullList = Object.assign({}, filterCustom, filterTopLevel);
    for (let key in fullList) {
      filterList[key] = [];
      for (let option in fullList[key]) {
        filterList[key].push({ text: option, value: option });
      }
    }

    const dive = (path, item) => {
      let value = retrieve(item, path);
      if (!value || typeof value !== 'object') {
        columnPathDirectory[path[path.length - 1]] = path.join('.');
        return;
      }

      if (Array.isArray(item)) {
        if (!item[0] || typeof item[0] !== 'object') {
          columnPathDirectory[path[path.length - 1]] = path.join('.');
          return;
        }
      }

      for (let key in value) {
        dive([...path, key], item);
      }
    };

    for (let key in data[0]) {
      if (Array.isArray(data[0][key])) {
        if (!data[0][key][0] || typeof data[0][key][0] !== 'object') {
          columnPathDirectory[key] = key;
          continue;
        }
      }
      dive([key], data[0]);
    }

    const storedActive =
      window.localStorage.getItem('suiteActiveColumns') &&
      JSON.parse(window.localStorage.getItem('suiteActiveColumns'));
    const storedOrder =
      window.localStorage.getItem('suiteColumnList') &&
      JSON.parse(window.localStorage.getItem('suiteColumnList'));
    let filteredOrder;
    let activeColumns = [];
    let columnList = [];
    let columnDirectory = {};
    for (let key in columnPathDirectory) {
      let item = {
        title: key.charAt(0).toUpperCase() + key.slice(1),
        dataIndex: columnPathDirectory[key],
        key: key,
        sorter: true,
        render: this.renderColumn,
      };

      columnDirectory[key] = item;

      columnList.push(key);

      // *** Pull from local storage here
      if (!storedActive || (storedActive && !storedActive.length)) {
        activeColumns.push(key);
      }
    }

    if (storedOrder && Array.isArray(storedOrder)) {
      let missingItems = columnList.filter(
        (item) => storedOrder.indexOf(item) === -1
      );
      filteredOrder = storedOrder.filter(
        (item) => columnList.indexOf(item) !== -1
      );
      if (missingItems.length) {
        filteredOrder = filteredOrder.concat(missingItems);
      }
    }

    this.setState({
      columnDirectory: columnDirectory,
      columnList: filteredOrder || columnList,
      activeColumns: storedActive || activeColumns,
      filteredData: filterResults(this.state.search, data, columnDirectory),
      loading: false,
      data,
      channels,
      filterList,
    });
  };

  rowSelection = {
    onChange: (selectedRowKeys, rowSelections) => {
      this.setState({
        selectedRowKeys,
        rowSelections,
      });
    },
  };

  handleTableChange = (pagination, filters, sorter) => {
    const { field } = sorter;
    const { filteredData } = this.state;

    this.setState({
      filteredData: fieldSort(
        filteredData,
        field,
        sorter.order,
        field === 'price' || field === 'quantity'
      ),
      filteredInfo: filters,
      sortedInfo: sorter,
      pageInfo: pagination,
    });
  };

  onSearch = (tags) => {
    const filteredData = filterResults(
      tags,
      this.state.data,
      this.state.columnDirectory
    );
    this.setState({ search: tags, filteredData });
  };

  handleColumnChange = (ordered) => {
    this.setState({
      columnList: ordered,
    });
    window.localStorage.setItem('suiteColumnList', JSON.stringify(ordered));
  };

  handleColumnSelectionChange = (actives) => {
    this.setState({
      activeColumns: actives,
    });
    window.localStorage.setItem('suiteActiveColumns', JSON.stringify(actives));
  };

  clearSelections = () => {
    this.setState({ selectedRowKeys: [], rowSelections: [] });
  };

  toggleEdit = (status) => {
    this.setState((state) => {
      return {
        editing: !state.editing,
      };
    });
    if (status === 'edited') {
      // clear selections here and re fetch data
      this.clearSelections();
      this.fetch();
    }
  };

  togglePublish = () => {
    this.setState((prevState) => ({ publishing: !prevState.publishing }));
  };

  toggleCancel = () => {
    this.setState((prevState) => ({ cancelling: !prevState.cancelling }));
  };

  exportXlsx = () => {
    const cols = JSON.parse(window.localStorage.getItem('suiteActiveColumns'));
    const data = this.state.filteredData.map((item, idx) => {
      const filteredItem = [];
      cols.forEach((heading) => {
        if (item[heading]) {
          filteredItem.push(item[heading].toString());
          return;
        }
        if (item.custom && item.custom[heading]) {
          filteredItem.push(item.custom[heading].toString());
          return;
        }
        filteredItem.push('');
      });
      return filteredItem;
    });
    data.unshift(cols);

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SheetJS');

    XLSX.writeFile(wb, `listings_${new Date().valueOf()}.xlsx`);
  };

  renderColumn = (text, record, index) => {
    if (typeof text === 'boolean') {
      return text ? 'Yes' : 'No';
    }

    return text;
  };

  render() {
    const {
      channels,
      sortedInfo,
      pageInfo,
      filteredInfo,
      loading,
      filteredData,
      columnDirectory,
      columnList,
      activeColumns,
      editing,
      publishing,
      cancelling,
      rowSelections = [],
      selectedRowKeys,
      filterList,
    } = this.state;
    this.rowSelection.selectedRowKeys = selectedRowKeys;

    const hasSelection = !!rowSelections.length;
    const displayColumns = [];
    columnList.forEach((colKey) => {
      const column = columnDirectory[colKey];
      if (!column.key || activeColumns.indexOf(column.key) === -1) {
        return;
      }

      if (filterList[column.key] && filterList[column.key].length > 1) {
        column.filters = filterList[column.key];
        column.filteredValue = filteredInfo[column.key] || null;
        column.onFilter = (value, record) => {
          return (
            (record[column.key] && record[column.key].toString() === value) ||
            (record.custom &&
              record.custom[column.key] &&
              record.custom[column.key].toString() === value)
          );
        };
      }

      if (colKey === 'listings') {
        displayColumns.push({
          ...column,
          sortOrder:
            (column.sorter &&
              sortedInfo.columnKey === column.key &&
              sortedInfo.order) ||
            false,
          render: (value, row) => (
            <ChannelListings value={value} channels={channels} />
          ),
        });
        return;
      }

      displayColumns.push({
        ...column,
        sortOrder:
          (column.sorter &&
            sortedInfo.columnKey === column.key &&
            sortedInfo.order) ||
          false,
      });
    });

    return (
      <main>
        <div className="container__listings container__listings--left">
          <Button
            type="primary"
            className="item__listings--left"
            onClick={this.toggleEdit}
            disabled={!hasSelection}
          >
            Edit
          </Button>
          <Button
            type="primary"
            className="item__listings--left"
            onClick={this.togglePublish}
            disabled={!hasSelection}
          >
            Publish
          </Button>
          <Button
            type="primary"
            className="item__listings--left"
            onClick={this.toggleCancel}
            disabled={!hasSelection}
          >
            Remove Listing
          </Button>
          <EditListingModal
            editing={editing}
            onClose={this.toggleEdit}
            selections={rowSelections}
            channels={this.state.channels}
            settings={this.state.settings}
            themes={this.state.themes}
            key={'edit' + selectedRowKeys.join('')}
          />
          <PublishModal
            open={publishing}
            onClose={this.togglePublish}
            onComplete={() => {
              this.setState({
                publishing: false,
                selectedRowKeys: [],
                rowSelections: [],
              });
              // maybe - fetch channels
            }}
            channels={this.state.channels}
            selections={rowSelections}
            key={'publish' + selectedRowKeys.join('')}
          />
          <CancelListingModal
            open={cancelling}
            onClose={this.toggleCancel}
            onComplete={() => {
              this.setState({
                cancelling: false,
                selectedRowKeys: [],
                rowSelections: [],
              });
              // maybe - fetch channels
            }}
            channels={this.state.channels}
            selections={rowSelections}
            key={'cancel' + selectedRowKeys.join('')}
          />
        </div>
        <div className="container__listings container__listings--right">
          <TagSearch
            placeholder="Search..."
            onChange={this.onSearch}
            value={this.state.search}
            className="item__listings--right search__listings"
          />
          <ColumnSelection
            columnList={columnList}
            activeColumns={activeColumns}
            columnReorder={this.handleColumnChange}
            activeReorder={this.handleColumnSelectionChange}
          />
          <Button className="item__listings--right" onClick={this.exportXlsx}>
            Export
          </Button>
        </div>
        <br />
        <br />
        <Table
          rowKey={(record) => `${record.sku}${record.remoteId}`}
          rowSelection={this.rowSelection}
          columns={displayColumns}
          dataSource={filteredData}
          loading={loading}
          onChange={this.handleTableChange}
          scroll={{ x: '100%' }}
          className="table__listings"
          pagination={pageInfo}
          onRow={(record) => ({
            onClick: () => {
              this.setState({
                rowSelections: [record],
                selectedRowKeys: [record.sku],
              });
              this.toggleEdit();
            },
          })}
        />
      </main>
    );
  }
}

export default Listings;
